import { useMemo, useState } from "react"

// Reuses the DIY composer's two-column ingredient dropdown (dark service box on
// the left, scrollable list on the right). It's an untyped JS component.
import IngredientSuggestions from "shared/components/stored_fields/ingredient_text_field/ingredient_suggestions"
import { getCSSVar } from "shared/lib/utils"

import type { TextInputIngredientGroup } from "../types"
import styles from "./ingredient_picker.module.scss"

interface Props {
  groups: TextInputIngredientGroup[]
  onSelect: (filterCode: string) => void
  onClose: () => void
}

const groupKey = (group: TextInputIngredientGroup) => `${group.step_kind}-${group.step_slug}`

const IngredientPicker = ({ groups, onSelect, onClose }: Props) => {
  const [selectedKey, setSelectedKey] = useState(groupKey(groups[0]))

  const selectedGroup = groups.find(group => groupKey(group) === selectedKey) ?? groups[0]

  const filterCodeByName = useMemo(() => {
    const map: Record<string, string> = {}
    selectedGroup.ingredients.forEach(ingredient => {
      map[ingredient.name] = ingredient.filter_code
    })
    return map
  }, [selectedGroup])

  // IngredientSuggestions expects a service-shaped object: a brand color for the
  // left box / selected row, plus an icon source it falls back through.
  const service = {
    name: selectedGroup.service_name,
    background_color: selectedGroup.brand_color
      ? `#${selectedGroup.brand_color}`
      : getCSSVar("primary-text") || "#111111",
    icon_url: selectedGroup.icon_url,
  }

  // Selector to switch between services/steps; only rendered by
  // IngredientSuggestions when platformComposer is true (multiple groups).
  const selector = (
    <select>
      {groups.map(group => (
        <option key={groupKey(group)} value={groupKey(group)}>
          {group.service_name} · {group.step_name}
        </option>
      ))}
    </select>
  )

  return (
    <div className={`ingredients-text-field ${styles["wrapper"]}`}>
      <IngredientSuggestions
        service={service}
        trigger={{ name: selectedGroup.service_name }}
        suggestions={selectedGroup.ingredients.map(ingredient => ingredient.name)}
        onSelect={(name: string) => onSelect(filterCodeByName[name])}
        onClose={onClose}
        platformComposer={groups.length > 1}
        hideIngredientNamespaces={true}
        selector={selector}
        onChange={(key: string) => setSelectedKey(key)}
        lastSelectedSuggestion={selectedKey}
        handleLastSelectedSuggestion={(key: string) => setSelectedKey(key)}
        shouldHideSuggestions={(hidden: boolean) => {
          if (hidden) onClose()
        }}
      />
    </div>
  )
}

export default IngredientPicker
